type Props = {
  skillName: string;
  skillUrl: string;
  level: string;
  bullets: string[];
  hideYouCanPrefix?: boolean;
};

const normalizeLevel = (value: string): string => String(value || '').trim().toLowerCase();

const getFilledBlocks = (level: string): number => {
  const normalized = normalizeLevel(level);
  if (normalized === 'awareness') return 1;
  if (normalized === 'working') return 2;
  if (normalized === 'practitioner') return 3;
  if (normalized === 'expert') return 4;
  return 0;
};

const ordinalByIndex: Record<number, string> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
};

function GdadSkillDefinition({ skillName, skillUrl, level, bullets, hideYouCanPrefix }: Props) {
  const filledBlocks = getFilledBlocks(level);
  const normalizedLevel = normalizeLevel(level);
  const descriptionWordCount = bullets.reduce((total, bullet) => {
    const trimmed = String(bullet || '').trim();
    if (!trimmed) return total;
    return total + trimmed.split(/\s+/).length;
  }, 0);
  const useSmallerDescriptionText = descriptionWordCount >= 100;
  const descriptionTextClassName = useSmallerDescriptionText
    ? 'govuk-body govuk-body-s'
    : 'govuk-body';
  const levelLabel = normalizedLevel
    ? normalizedLevel.charAt(0).toUpperCase() + normalizedLevel.slice(1)
    : level;

  const ordinal = ordinalByIndex[filledBlocks];
  const visuallyHiddenText =
    filledBlocks > 0 && ordinal
      ? `${levelLabel} is the ${ordinal} of 4 ascending skill levels`
      : `${levelLabel} skill level indicator`;

  return (
    <table className="govuk-table">
      <thead className="govuk-table__head">
        <tr className="govuk-table__row">
          <th scope="col" className="govuk-table__header">
            Skill
          </th>
          <th scope="col" className="govuk-table__header govuk-!-width-two-thirds">
            Description
          </th>
        </tr>
      </thead>
      <tbody className="govuk-table__body">
        <tr className="govuk-table__row">
          <th scope="row" className="govuk-table__header">
            <p className="govuk-!-margin-bottom-1 govuk-!-font-weight-bold">
              <a
                href={skillUrl}
                className="govuk-link skill-name"
                target="_blank"
                rel="noopener noreferrer"
              >
                {skillName}
              </a>
            </p>
            <p className="govuk-body govuk-!-font-size-19">Level: {normalizedLevel}</p>
            <span className="govuk-visually-hidden">{visuallyHiddenText}</span>
            <div className="progress-bar__container">
              {Array.from({ length: 4 }, (_, idx) => {
                const isFilled = idx < filledBlocks;
                const isLastFilled = filledBlocks > 0 && idx === filledBlocks - 1;

                // Match the DDaT source: awareness/working show greys only; practitioner/expert show the
                // "current" level as green.
                const showGreen = isLastFilled && filledBlocks >= 3;
                const className = isFilled
                  ? showGreen
                    ? 'progress-bar__green'
                    : 'progress-bar__grey'
                  : 'progress-bar__white';

                return [
                  <div key={`level-block-${idx}`} className={className} aria-hidden="true" />,
                  idx < 3 ? (
                    <div key={`level-divider-${idx}`} className="progress-bar__divider" aria-hidden="true" />
                  ) : null,
                ];
              })}
            </div>
          </th>

          <td className="govuk-table__cell">
            <p className="govuk-!-margin-bottom-1"></p>
            {!hideYouCanPrefix && <p className="govuk-body">You can:</p>}
            <ul className="govuk-list govuk-list--bullet">
              {bullets.map((bullet, index) => (
                <li className={descriptionTextClassName} key={`${skillName}-${index}`}>
                  {bullet}
                </li>
              ))}
            </ul>
            <p className="govuk-body"></p>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export default GdadSkillDefinition;

